import React, { useContext, ReactElement, useCallback } from 'react';

import PropTypes from 'prop-types';

import {
  StylableComponent,
  stylePropType,
  useLocale,
} from '@adsk/alloy-react-helpers';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleChevronLeftIcon,
  DoubleChevronRightIcon,
} from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';

import PaginationButton from './PaginationButton';
import TableSizeThemeContext from './TableSizeThemeContext';

type TablePaginationFooterProps = StylableComponent<HTMLDivElement> & {
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageCount: number;
  gotoPage: (pageIndex: number) => void;
  state: {
    pageIndex: number;
    pageSize: number;
    totalDataCount: number;
  };
};

const TablePaginationFooter = ({
  canPreviousPage,
  canNextPage,
  pageCount,
  gotoPage,
  state,
  style,
  className,
}: TablePaginationFooterProps): ReactElement => {
  const { tableSizeTheme } = useContext(TableSizeThemeContext);
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { pageIndex, pageSize, totalDataCount } = state;

  return (
    <div
      className={className}
      css={[
        {
          position: 'sticky',
          bottom: 0,
          left: 0,
          backgroundColor: theme.colors.white,
          borderTop: `1px solid ${theme.colors.charcoal200}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: tableSizeTheme.rowHeight,
          padding: `0 ${tableSizeTheme.cellPadding}px`,
        },
        style,
      ]}
    >
      <div style={{ color: theme.colors.charcoal700 }}>
        {T.translate('SHOWING', {
          itemRangeStart: pageSize * pageIndex + 1,
          itemRangeEnd: Math.min(
            pageSize * pageIndex + pageSize,
            totalDataCount
          ),
          itemCount: totalDataCount,
        })}
      </div>
      <div css={[{ display: 'flex', alignItems: 'center' }]}>
        <PaginationButton
          disabled={!canPreviousPage}
          renderIcon={useCallback(
            () => (
              <DoubleChevronLeftIcon />
            ),
            []
          )}
          onClick={() => gotoPage(0)}
          style={{ marginRight: 4 }}
        />
        <PaginationButton
          disabled={!canPreviousPage}
          renderIcon={useCallback(
            () => (
              <ChevronLeftIcon />
            ),
            []
          )}
          onClick={() => gotoPage(pageIndex - 1)}
        />
        <div css={[{ margin: '0 12px' }]}>
          {T.translate('PAGE_COUNT', {
            currentPage: pageIndex + 1,
            totalPages: pageCount,
          })}
        </div>
        <PaginationButton
          disabled={!canNextPage}
          renderIcon={useCallback(
            () => (
              <ChevronRightIcon />
            ),
            []
          )}
          onClick={() => gotoPage(pageIndex + 1)}
        />
        <PaginationButton
          disabled={!canNextPage}
          renderIcon={useCallback(
            () => (
              <DoubleChevronRightIcon />
            ),
            []
          )}
          onClick={() => gotoPage(pageCount - 1)}
          style={{ marginLeft: 4 }}
        />
      </div>
    </div>
  );
};

TablePaginationFooter.propTypes = {
  canPreviousPage: PropTypes.bool.isRequired,
  canNextPage: PropTypes.bool.isRequired,
  pageCount: PropTypes.number.isRequired,
  gotoPage: PropTypes.func.isRequired,
  state: PropTypes.shape({
    pageIndex: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalDataCount: PropTypes.number.isRequired,
  }),
  style: stylePropType,
};

export default TablePaginationFooter;
